import type { CharacterProfile, ChatExportFields, WorldDocument } from "@shared/schema";
import type { APIKeyConfig, ValidationResult } from "@/types/api-key";
import type { ProviderResponse } from "@/types/usage";
import type { ContentLanguageCode } from "@/constants/content-languages";

export type { ProviderResponse };

/**
 * Normalize a provider result that might be a raw value or a ProviderResponse
 */
export function unwrapProviderResponse<T>(
  result: T | ProviderResponse<T>
): ProviderResponse<T> {
  if (result && typeof result === 'object' && 'value' in result) {
    const providerResult = result as ProviderResponse<T>;
    return { value: providerResult.value, usage: providerResult.usage };
  }
  return { value: result as T, usage: undefined };
}

/**
 * Image generation prompts returned by prompt planning operations
 */
export interface ImagePlanPrompts {
  naturalPrompt: string;
  stableDiffusionPrompt: string;
  negativePrompt?: string;
}

/**
 * Options for text generation operations
 */
export interface TextGenerationOptions {
  signal?: AbortSignal;
  customPrompts?: { system?: string; user?: string };
  logUsage?: (details: any) => void;
  contentLanguage?: ContentLanguageCode | string | null;
}

/**
 * Options for image generation operations
 */
export interface ImageGenerationOptions {
  signal?: AbortSignal;
  logUsage?: (details: any) => void;
  referenceImageData?: string;
  referenceImageUrl?: string;
  referenceImageDataArray?: string[];
  referenceImageUrlArray?: string[];
}

/**
 * Options for roleplay operations
 */
export interface RoleplayOptions {
  signal?: AbortSignal;
  customPrompts?: { system?: string };
  logUsage?: (details: any) => void;
  contentLanguage?: ContentLanguageCode | string | null;
}

/**
 * Options for AI edit operations
 */
export interface EditOptions {
  signal?: AbortSignal;
  customPrompts?: { system: string; user: string };
  variant?: string;
  logUsage?: (details: any) => void;
  contentLanguage?: ContentLanguageCode | string | null;
}

/**
 * Core interface that all AI providers must implement
 *
 * This interface defines the contract for interacting with AI providers.
 * Providers implement only the capabilities they support: text methods are
 * optional for image-only providers, image methods for text-only providers.
 */
export interface AIProvider {
  /**
   * Validate an API key for this provider
   */
  validateApiKey(config: {
    apiKey: string;
    model?: string;
    customEndpoint?: string;
  }): Promise<ValidationResult>;

  /**
   * Generate a complete character profile (optional - only for text providers)
   */
  generateCharacterProfile?(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    worldDocument?: WorldDocument,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<CharacterProfile>>;

  /**
   * Generate chat export fields (first message, example dialogue)
   */
  generateChatExportFields?(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<ChatExportFields>>;

  /**
   * Generate image plan prompts (for image generation preprocessing)
   */
  generateImagePlanPrompts?(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<ImagePlanPrompts>>;

  /**
   * Rewrite a single character field
   */
  rewriteCharacterField?(
    instructions: string,
    config: APIKeyConfig,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<string>>;

  /**
   * Edit character with AI instructions (returns JSON patch)
   */
  editCharacter?(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    opts?: EditOptions
  ): Promise<ProviderResponse<any>>;

  /**
   * Generate a roleplay reply
   */
  generateRoleplayReply?(
    params: {
      system: string;
      messages: Array<{ role: 'user' | 'assistant'; content: string }>;
    },
    config: APIKeyConfig,
    opts?: RoleplayOptions
  ): Promise<ProviderResponse<string>>;

  /**
   * Generate an image from a prompt (optional - only for providers with image generation)
   */
  generateImage?(
    prompt: string,
    config: APIKeyConfig,
    opts?: ImageGenerationOptions
  ): Promise<ProviderResponse<{ imageData: string }>>;

  /**
   * Edit an image based on a reference and prompt (optional)
   * @param referenceImage - Single reference image or array of reference images for multi-reference support
   */
  generateImageEdit?(
    referenceImage: string | string[],
    prompt: string,
    config: APIKeyConfig,
    opts?: ImageGenerationOptions
  ): Promise<ProviderResponse<{ imageData: string }>>;
}

/**
 * Base class for AI providers that provides common functionality
 *
 * Provider implementations should extend this class and implement
 * the required abstract methods. The base class provides:
 * - Common validation logic
 * - Standardized error handling
 * - Utility methods for common operations
 */
export abstract class BaseProvider implements AIProvider {
  /**
   * Validate that required config fields are present
   */
  protected validateConfig(config: APIKeyConfig): void {
    if (!config.apiKey || config.apiKey.trim().length === 0) {
      throw new Error('API key is required');
    }
    if (!config.model || config.model.trim().length === 0) {
      throw new Error('Model is required');
    }
  }

  /**
   * Check if the operation was aborted
   */
  protected checkAborted(signal?: AbortSignal): void {
    if (signal?.aborted) {
      const error = new Error('Operation aborted');
      (error as any).name = 'AbortError';
      throw error;
    }
  }

  // Abstract methods that must be implemented by each provider
  abstract validateApiKey(config: {
    apiKey: string;
    model?: string;
    customEndpoint?: string;
  }): Promise<ValidationResult>;

  abstract generateCharacterProfile(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    worldDocument?: WorldDocument,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<CharacterProfile>>;

  abstract generateChatExportFields(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<ChatExportFields>>;

  abstract generateImagePlanPrompts(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<ImagePlanPrompts>>;

  abstract rewriteCharacterField(
    instructions: string,
    config: APIKeyConfig,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<string>>;

  abstract editCharacter(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    opts?: EditOptions
  ): Promise<ProviderResponse<any>>;

  abstract generateRoleplayReply(
    params: {
      system: string;
      messages: Array<{ role: 'user' | 'assistant'; content: string }>;
    },
    config: APIKeyConfig,
    opts?: RoleplayOptions
  ): Promise<ProviderResponse<string>>;

  // Optional methods - providers can implement if they support the capability
  generateImage?(
    prompt: string,
    config: APIKeyConfig,
    opts?: ImageGenerationOptions
  ): Promise<ProviderResponse<{ imageData: string }>>;

  generateImageEdit?(
    referenceImage: string,
    prompt: string,
    config: APIKeyConfig,
    opts?: ImageGenerationOptions
  ): Promise<ProviderResponse<{ imageData: string }>>;
}

/**
 * Helper to determine if a provider supports image generation
 */
export function supportsImageGeneration(provider: AIProvider): provider is AIProvider & {
  generateImage: NonNullable<AIProvider['generateImage']>;
} {
  return typeof provider.generateImage === 'function';
}

/**
 * Helper to determine if a provider supports image editing
 */
export function supportsImageEdit(provider: AIProvider): provider is AIProvider & {
  generateImageEdit: NonNullable<AIProvider['generateImageEdit']>;
} {
  return typeof provider.generateImageEdit === 'function';
}
